import { useSavedBlockPoIsQuery } from '@/__generated__/graphql';
import { useGetStructureId } from '@/hooks/useGetStructureId';
import { useSelectedAssemblyTemplateId } from '@/components/Analysis/hooks';
import * as localState from '../state';
import { useState } from 'react';
import { Box, Stack, Typography } from '@mui/material';
import { CustomBlockListHeader } from './CustomBlockListHeader';
import { CustomBlockListSearch } from './CustomBlockListSearch';
import { CustomSavedBlocksListItem } from './CustomSavedBlocksListItem';
import { useSetAtom } from 'jotai';
import { SavedBlockSortingHeader } from './SavedBlockSortingHeader';
import { PageLoader } from '@/components/shared/PageLoader';
import { BlockListError } from './BlockListError';

export const SavedBlocksList = () => {
  const structureId = useGetStructureId();
  const selectedAssemblyTemplateId = useSelectedAssemblyTemplateId();

  const setSelectedSavedBlockId = useSetAtom(localState.selectedSavedBlockId);
  const setAllSavedBlocks = useSetAtom(localState.allSavedBlocks);

  const [selectedMetadata, setSelectedMetadata] = useState<string>('area');

  const { data, loading, error } = useSavedBlockPoIsQuery({
    variables: {
      input: {
        structureId,
        assemblyTemplateId: selectedAssemblyTemplateId || '',
        type: 'WORK_SCOPE',
      },
    },
    skip: !structureId,
    onCompleted: (res) => {
      setAllSavedBlocks(res.pointOfInterests);
    },
  });

  const handleRowClick = (poiId: string | undefined) => {
    setSelectedSavedBlockId(poiId);
  };

  const handleSortChange = (sortBy: string | undefined, sortDir: number | undefined) => {
    if (sortBy && sortDir) {
      setSelectedMetadata(sortBy);
    }
  };

  if (error) {
    return <BlockListError message={error.message} />;
  }

  if (loading) {
    return (
      <Box p={2}>
        <PageLoader />
      </Box>
    );
  }

  const pois = data?.pointOfInterests ?? [];
  const total = data?.pointOfInterestsCount?.count ?? 0;

  if (pois.length === 0) {
    return (
      <Box p={2}>
        <Typography variant="h5" color="info" textAlign="center" mt={5}>
          No saved blocks found
        </Typography>
      </Box>
    );
  }

  return (
    <Stack height="100%">
      <CustomBlockListHeader count={total || undefined} mt={2} mb={1} px={1} />
      <CustomBlockListSearch sx={{ px: 1, my: 2 }} isSavedBlocks />
      <SavedBlockSortingHeader onSortChange={handleSortChange} />

      <Stack
        mb={2}
        sx={{
          overflowY: 'auto',
        }}
      >
        {pois.map((item) => (
          <CustomSavedBlocksListItem
            key={item.id}
            poi={item}
            onRowClick={() => handleRowClick(item.id)}
            selectedMetadata={selectedMetadata}
          />
        ))}
      </Stack>
    </Stack>
  );
};
